import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import { relTime } from "../lib/format";
import TopBar from "../components/TopBar";
import StatusPill from "../components/StatusPill";
import EmptyState from "../components/EmptyState";

export default function MissionsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [missions, setMissions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const canWrite = user?.role === "director" || user?.role === "admin";

  useEffect(() => {
    (async () => {
      const [{ data: ms }, { data: pr }] = await Promise.all([
        api.get("/missions"),
        api.get("/projects"),
      ]);
      setMissions(ms);
      setProjects(pr);
    })();
  }, []);

  const filtered = useMemo(
    () => missions.filter((m) => statusFilter === "all" || m.status === statusFilter),
    [missions, statusFilter]
  );

  return (
    <>
      <TopBar
        title="Missions"
        subtitle="All missions across projects."
        actions={
          canWrite ? (
            <button
              className="btn primary"
              onClick={() => navigate("/commandlinc")}
              data-testid="missions-new-btn"
            >
              <Plus size={15} /> New Mission
            </button>
          ) : null
        }
      />

      <div className="panel">
        <div className="filters">
          <div className="field">
            <label style={{ marginBottom: 3 }}>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="filter-status"
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="in_progress">In progress</option>
              <option value="in_review">In review</option>
              <option value="blocked">Blocked</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <span className="hint">{filtered.length} shown</span>
        </div>

        <div className="mission-table">
          <div className="mission-row headings">
            <span>MISSION</span>
            <span>PROJECT</span>
            <span>PRIORITY</span>
            <span>STATUS</span>
            <span>UPDATED</span>
          </div>
          {filtered.length === 0 ? (
            <EmptyState title="No missions match this filter." />
          ) : (
            filtered.map((m) => (
              <Link
                key={m.id}
                to={`/commandlinc/missions/${m.id}`}
                className="mission-row button"
                data-testid={`missions-row-${m.id}`}
                style={{ textDecoration: "none", display: "grid" }}
              >
                <span className="title">
                  <b>
                    <span className="id-chip">#{m.id.slice(0, 4).toUpperCase()}</span> {m.title}
                  </b>
                  <small>{m.brief}</small>
                </span>
                <span>{projects.find((p) => p.id === m.project_id)?.name || "—"}</span>
                <span className="hint">{m.priority}</span>
                <span><StatusPill status={m.status} /></span>
                <span className="hint">{relTime(m.updated_at)}</span>
              </Link>
            ))
          )}
        </div>
      </div>
    </>
  );
}
